/**
 * The subject as the zones kernel wants it: typed arrays in BIND-WORLD and the
 * skeleton as named segments (ADR-031 §4 — the kernel stays pixel-free and
 * engine-free; this is the one place three's objects become numbers for it).
 *
 * BIND-WORLD MEANS `position · bindMatrix` — the same definition the hand
 * pipeline's `isolateHandRegion` and the app's `bindPoseVertices` use, so a
 * zone measured here lands in the space a pin is stored in (ADR-022 §5.1).
 * Bone positions come from the skeleton's inverse-bind matrices inverted —
 * the same quantity the kernel's `RigDescriptor.worldBind` is derived from
 * (ADR-003 §2b) — so vertices and segments share one frame by construction.
 *
 * ONE SKINNED MESH. Multi-mesh subjects (VRM body + clothing) are out of T1's
 * scope; `soleSkinnedMesh` refuses them by name rather than silently taking
 * the first (the standing rule of the hand pipeline).
 *
 * `paint: 'file'` reads the skin and skeleton AS THE FILE SHIPPED THEM (the
 * load door's record, `file-paint`) instead of as the run has re-painted them:
 * the tail detector's owner cue must not read the pelvis re-own as tail
 * (2026-09-23). Positions and faces are the same either way, so a zone's face
 * indices and fingerprint do not depend on the choice. A scene no recording
 * door loaded falls back to the current paint.
 *
 * Depends on: three, hands/isolate-hand (soleSkinnedMesh), zones/file-paint.
 * Depended on by: the app's zone proposer and the tiger specimen probe.
 */
import { Matrix3, Matrix4, Vector3, type Object3D } from 'three';
import { soleSkinnedMesh } from '../hands/isolate-hand.js';
import { filePaintOf } from './file-paint.js';

export interface SubjectArrays {
  readonly positions: Float32Array;
  readonly indices: Uint32Array;
  readonly skin: { readonly index: Uint16Array; readonly weight: Float32Array } | null;
  /** Skeleton bones in SKIN ORDER (so `skin.index` addresses them), with parent names and bind-world positions. */
  readonly bones: readonly { readonly name: string; readonly parent: string | null; readonly worldPosition: { x: number; y: number; z: number } }[];
}

export class SubjectArraysRefusal extends Error {}

export interface SubjectArraysOptions {
  /** `current` (default): the skin as the run left it. `file`: as the load door recorded it. */
  readonly paint?: 'current' | 'file';
}

export function subjectArrays(scene: Object3D, options: SubjectArraysOptions = {}): SubjectArrays {
  const mesh = soleSkinnedMesh(scene);
  const geometry = mesh.geometry;
  const position = geometry.getAttribute('position');
  if (!position) throw new SubjectArraysRefusal('the subject mesh has no position attribute');
  const index = geometry.getIndex();
  const bind = new Matrix4().copy(mesh.bindMatrix);
  const v = new Vector3();

  const positions = new Float32Array(position.count * 3);
  for (let i = 0; i < position.count; i++) {
    v.fromBufferAttribute(position, i).applyMatrix4(bind);
    positions[i * 3] = v.x;
    positions[i * 3 + 1] = v.y;
    positions[i * 3 + 2] = v.z;
  }

  let indices: Uint32Array;
  if (index) {
    indices = new Uint32Array(index.count);
    for (let i = 0; i < index.count; i++) indices[i] = index.getX(i);
  } else {
    // Non-indexed: every three vertices are a face, in order.
    indices = new Uint32Array(position.count);
    for (let i = 0; i < position.count; i++) indices[i] = i;
  }

  const skinIndex = geometry.getAttribute('skinIndex');
  const skinWeight = geometry.getAttribute('skinWeight');
  let skin: SubjectArrays['skin'] = null;
  const skeleton = mesh.skeleton;
  const filed = options.paint === 'file' ? filePaintOf(mesh) : null;
  // The file's bones must all still be in the skeleton (grafts APPEND; a door
  // that dropped one would make the file's indices address the wrong bone).
  const slotOf = new Map(skeleton.bones.map((b, i) => [b as Object3D, i] as const));
  const useFile = filed !== null && filed.index.length === position.count * 4 && filed.bones.every((b) => slotOf.has(b));
  if (useFile) {
    skin = { index: filed.index.slice(), weight: filed.weight.slice() };
  } else if (skinIndex && skinWeight) {
    const si = new Uint16Array(position.count * 4);
    const sw = new Float32Array(position.count * 4);
    for (let i = 0; i < position.count; i++) {
      for (let k = 0; k < 4; k++) {
        si[i * 4 + k] = skinIndex.getComponent(i, k);
        sw[i * 4 + k] = skinWeight.getComponent(i, k);
      }
    }
    skin = { index: si, weight: sw };
  }

  // Skeleton membership by OBJECT, not name — an imported node may wear a
  // bone's name without being one.
  const skinBones: readonly Object3D[] = useFile ? filed.bones : skeleton.bones;
  const skeletonBones = new Set<Object3D>(skinBones);
  const world = new Matrix4();
  const bones = skinBones.map((bone) => {
    // inverse(boneInverse) = the bone's world matrix at bind (ADR-003 §2b).
    world.copy(skeleton.boneInverses[slotOf.get(bone)!]!).invert();
    const p = new Vector3().setFromMatrixPosition(world);
    // The nearest ANCESTOR that is a skeleton bone — a joint parented through
    // an intermediary Object3D (a group, an empty) still hangs off the bone
    // above that node, and dropping the span would leave its flesh reading as
    // unarmatured (Codex P2, #136).
    let ancestor: Object3D | null = bone.parent;
    while (ancestor !== null && !skeletonBones.has(ancestor)) ancestor = ancestor.parent;
    return { name: bone.name, parent: ancestor === null ? null : ancestor.name, worldPosition: { x: p.x, y: p.y, z: p.z } };
  });

  return { positions, indices, skin, bones };
}

/**
 * Write turned BIND-WORLD positions back into the sole skinned mesh (the
 * inverse of {@link subjectArrays}' `position · bindMatrix`), turning each
 * moved vertex's normal (and tangent) by the same yaw about world +Y — the
 * tail re-centre (kernel tails/recentre). `yawRad` maps vertex → its turn.
 */
export function writeSubjectBindPositions(scene: Object3D, positions: ArrayLike<number>, yawRad: ReadonlyMap<number, number>): void {
  const mesh = soleSkinnedMesh(scene);
  const geometry = mesh.geometry;
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  const tangent = geometry.getAttribute('tangent');
  const inv = new Matrix4().copy(mesh.bindMatrix).invert();
  const toWorld = new Matrix3().setFromMatrix4(mesh.bindMatrix);
  const toLocal = new Matrix3().setFromMatrix4(inv);
  const v = new Vector3();
  const turn = (a: number, w: Vector3): Vector3 => {
    const c = Math.cos(a);
    const s = Math.sin(a);
    return w.set(c * w.x + s * w.z, w.y, -s * w.x + c * w.z);
  };
  for (const [i, a] of yawRad) {
    v.set(positions[i * 3]!, positions[i * 3 + 1]!, positions[i * 3 + 2]!).applyMatrix4(inv);
    position.setXYZ(i, v.x, v.y, v.z);
    if (normal) {
      turn(a, v.fromBufferAttribute(normal, i).applyMatrix3(toWorld)).applyMatrix3(toLocal).normalize();
      normal.setXYZ(i, v.x, v.y, v.z);
    }
    if (tangent) {
      v.set(tangent.getX(i), tangent.getY(i), tangent.getZ(i));
      turn(a, v.applyMatrix3(toWorld)).applyMatrix3(toLocal).normalize();
      tangent.setXYZW(i, v.x, v.y, v.z, tangent.getW(i));
    }
  }
  position.needsUpdate = true;
  if (normal) normal.needsUpdate = true;
  if (tangent) tangent.needsUpdate = true;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

/**
 * Write SKIN-ORDER influences back into the sole skinned mesh for `vertices`
 * only (the inverse of {@link subjectArrays}' current-paint read: its
 * `skin.index` addresses `skeleton.bones`, and so does this). Every other
 * vertex is left exactly as it was — not re-normalised, not rewritten — which
 * is what makes "outside the region nothing changes" a property of the door.
 * ADR-039 T2a's hair weights land here. Fail-loud: a slot outside the skeleton,
 * a non-finite or negative weight, or a vertex whose four weights do not sum to
 * 1 (beyond float32 residue) is refused before anything is written.
 */
export function writeSubjectSkin(scene: Object3D, vertices: readonly number[], index: ArrayLike<number>, weight: ArrayLike<number>): void {
  const mesh = soleSkinnedMesh(scene);
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  if (!skinIndex || !skinWeight) throw new SubjectArraysRefusal('the subject mesh is not skinned — there is no paint to write');
  const bones = mesh.skeleton.bones.length;
  const issues: string[] = [];
  for (const v of vertices) {
    if (!Number.isInteger(v) || v < 0 || v >= skinIndex.count) {
      issues.push(`vertex ${v} is outside the mesh`);
      continue;
    }
    let sum = 0;
    for (let k = 0; k < 4; k++) {
      const s = index[v * 4 + k]!;
      const w = weight[v * 4 + k]!;
      if (!Number.isFinite(w) || w < 0) issues.push(`vertex ${v} slot ${k} has weight ${w}`);
      if (w > 0 && !(s >= 0 && s < bones)) issues.push(`vertex ${v} names bone slot ${s}, outside the skeleton's ${bones}`);
      sum += w;
    }
    if (Math.abs(sum - 1) > 1e-5) issues.push(`vertex ${v} weights sum to ${sum}`);
  }
  if (issues.length > 0) throw new SubjectArraysRefusal(`cannot write the skin: ${issues.slice(0, 5).join('; ')}${issues.length > 5 ? ` (+${issues.length - 5} more)` : ''}`);
  for (const v of vertices) {
    for (let k = 0; k < 4; k++) {
      skinIndex.setComponent(v, k, index[v * 4 + k]!);
      skinWeight.setComponent(v, k, weight[v * 4 + k]!);
    }
  }
  skinIndex.needsUpdate = true;
  skinWeight.needsUpdate = true;
}
